import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Collapse, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'

import { BEIGE_300, GRAY_200, GRAY_500, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUIIconButton } from 'components/common/MUIIconButton'
import { useDeleteDraft, useDrafts } from 'dataQueries/purchaseFlowDrafts.query'
import { isEmptyDraft, type DraftMeta } from 'utils/purchaseFlowDrafts'

import { PfClickableCard } from '../components/PfClickableCard.component'
import { useResumeDraft } from './useResumeDraft'

interface Props {
  /** Closes the surrounding drawer once a draft has been resumed. */
  onResumed: () => void
}

/**
 * The client's saved orders, listed under the property search: a row resumes its draft, the bin deletes it
 * after a second tap, and "Clear all" deletes every draft after a second tap. Renders nothing without drafts.
 */
export const DraftOrdersSection: React.FC<Props> = ({ onResumed }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const navigate = useNavigate()

  const draftsQuery = useDrafts()
  const deleteDraft = useDeleteDraft()
  const resume = useResumeDraft()

  const [isExpanded, setIsExpanded] = useState(true)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [isConfirmingClearAll, setIsConfirmingClearAll] = useState(false)
  // A second click while a draft is still loading would write the first draft's files into the second.
  const isResumingRef = useRef(false)

  // Most recently worked on first (QA-499).
  const drafts = useMemo(
    () => (draftsQuery.data ?? [])
      .filter((d) => !isEmptyDraft(d))
      .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)),
    [draftsQuery.data]
  )

  const handleResume = useCallback(async (draft: DraftMeta) => {
    if (isResumingRef.current) return
    isResumingRef.current = true
    try {
      const path = await resume(draft)
      if (!path) return
      onResumed()
      navigate(path)
    } finally {
      isResumingRef.current = false
    }
  }, [resume, onResumed, navigate])

  const handleDelete = useCallback((draftId: string) => {
    if (pendingDeleteId !== draftId) {
      setPendingDeleteId(draftId)
      return
    }
    deleteDraft.mutate(draftId)
    setPendingDeleteId(null)
  }, [pendingDeleteId, deleteDraft])

  const handleClearAll = useCallback(() => {
    if (!isConfirmingClearAll) {
      setIsConfirmingClearAll(true)
      return
    }
    drafts.forEach((draft) => deleteDraft.mutate(draft.draftId))
    setIsConfirmingClearAll(false)
  }, [isConfirmingClearAll, drafts, deleteDraft])

  const createdLabel = (draft: DraftMeta) => {
    try { return formatDistanceToNow(new Date(draft.createdAt || draft.updatedAt), { addSuffix: true }) } catch { return '' }
  }

  if (drafts.length === 0) return null

  return (
    <Stack sx={{ mt: '3.2rem' }} data-cy="pf-revamp-draft-orders">

      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setIsExpanded((open) => !open)}
        aria-expanded={isExpanded}
        sx={{ border: 'none', background: 'none', p: 0, font: 'inherit', cursor: 'pointer', textAlign: 'left' }}
      >
        <Typography variant="text-lg" color={GRAY_900} fontWeight={500}>
          {t('purchase_flow_revamp:drafts.section_title')}
        </Typography>
        <KeyboardArrowUpRoundedIcon
          sx={{ fontSize: '2.4rem', color: GRAY_900, transition: 'transform 0.2s', transform: isExpanded ? 'none' : 'rotate(180deg)' }}
        />
      </Stack>

      <Collapse in={isExpanded}>
        <Stack sx={{ mt: '1.2rem' }}>

          {drafts.map((draft) => {
            const isConfirmingDelete = pendingDeleteId === draft.draftId
            return (
              <PfClickableCard
                key={draft.draftId}
                data-cy="pf-revamp-draft-row"
                direction="row"
                alignItems="center"
                gap="1.2rem"
                onActivate={() => { void handleResume(draft) }}
                sx={{
                  py: '1.2rem',
                  px: '0.4rem',
                  borderBottom: `1px solid ${GRAY_200}`,
                  borderRadius: '0.4rem',
                  '&:hover': { backgroundColor: BEIGE_300 },
                }}
              >
                <Stack flex={1} gap="0.2rem" overflow="hidden">
                  <Typography
                    variant="text-md"
                    fontWeight={500}
                    color={draft.propertyAddress ? GRAY_900 : GRAY_500}
                    noWrap
                  >
                    {draft.propertyAddress ?? t('purchase_flow_revamp:drafts.unnamed_label')}
                  </Typography>
                  <Typography variant="text-sm" color={GRAY_700}>
                    {t('purchase_flow_revamp:drafts.created_prefix')} <Typography component="span" variant="text-sm" color={GRAY_900}>{createdLabel(draft)}</Typography>
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" gap="0.4rem" flexShrink={0} onClick={(event) => event.stopPropagation()}>
                  {isConfirmingDelete &&
                    <Typography variant="text-xs" color="error.main">
                      {t('purchase_flow_revamp:drafts.delete_confirm_label')}
                    </Typography>
                  }
                  <MUIIconButton
                    data-cy="pf-revamp-draft-delete"
                    variant="plain"
                    size="sm"
                    icon={<DeleteOutlineRoundedIcon />}
                    ariaLabel={t('purchase_flow_revamp:drafts.delete_aria', {
                      address: draft.propertyAddress ?? t('purchase_flow_revamp:drafts.unnamed_label'),
                    })}
                    danger={isConfirmingDelete}
                    disabled={deleteDraft.isPending}
                    onClick={() => handleDelete(draft.draftId)}
                  />
                </Stack>
              </PfClickableCard>
            )
          })}

          <Stack alignItems="center" sx={{ mt: '1.6rem' }}>
            <MUIButton
              type={isConfirmingClearAll ? 'orangeSecondaryBorder' : 'secondaryBorder'}
              size="sm"
              disabled={deleteDraft.isPending}
              onClick={handleClearAll}
              data-cy="pf-revamp-draft-clear-all"
            >
              {t(isConfirmingClearAll ? 'purchase_flow_revamp:drafts.clear_all_confirm' : 'purchase_flow_revamp:drafts.clear_all')}
            </MUIButton>
          </Stack>

        </Stack>
      </Collapse>

    </Stack>
  )
}
